/*!
 * Pahc cancel sales order data action.
 * Wraps the custom Retail Proxy operation so it can be
 * used both declaratively and imperatively from views/components.
 */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { pahcRequestSalesOrderCancellationAsync } from './DataActionExtension.g';
import { normalizeSalesIdForApi } from './normalize-sales-id';
import {
    IPahcSalesLine,
    IPahcSalesOrderCancellationRequestEntity,
    IPahcSalesOrderCancellationResponseEntity
} from './DataServiceEntities.g';

/**
 * Input for cancelling a sales order (optionally specific lines).
 */
export class CancelSalesOrderInput implements IActionInput {
    public readonly salesId: string;
    public readonly salesLines?: IPahcSalesLine[];

    public constructor(salesId: string, salesLines?: IPahcSalesLine[]) {
        this.salesId = salesId;
        this.salesLines = salesLines;
    }

    public getCacheKey = () => `PahcCancelSalesOrder-${this.salesId}`;

    public getCacheObjectType = () => 'PahcCancelSalesOrder';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * Generic input factory – not used directly in views but required
 * for wiring this up as a Commerce data action.
 */
const createInput = (input: ICreateActionContext): CancelSalesOrderInput => {
    // Best-effort lookup of SalesId from route/query; this makes the data
    // action usable in pipelines if needed, but views will normally construct
    // the input explicitly.
    const params = ((input.requestContext?.params ?? ({} as unknown)) as unknown) as Record<string, string | undefined>;
    const query = (input.requestContext?.query ?? ({} as unknown)) as Record<string, string | undefined>;
    const salesIdFromRoute = params.salesId ?? query.salesId ?? '';

    return new CancelSalesOrderInput(salesIdFromRoute);
};

/**
 * Core implementation that calls the custom Retail Proxy operation.
 */
export async function cancelSalesOrder(
    input: CancelSalesOrderInput,
    ctx: IActionContext
): Promise<IPahcSalesOrderCancellationResponseEntity> {
    const salesId = normalizeSalesIdForApi(input.salesId);
    if (!salesId) {
        throw new Error('[pahc-cancel-sales-order] SalesId is required');
    }

    const request: IPahcSalesOrderCancellationRequestEntity = {
        SalesId: salesId,
        SalesLines: input.salesLines
    };

    // Retail-proxy custom operation expects an `IContext` whose `callerContext`
    // contains a full `requestContext` (apiSettings, locale, etc.).
    // The Commerce `IActionContext.requestContext` already matches the
    // `ICallerContext.requestContext` shape, so we wrap it accordingly.
    const retailProxyContext: import('@msdyn365-commerce/retail-proxy').IContext = {
        callerContext: {
            requestContext: (ctx.requestContext as unknown) as import('@msdyn365-commerce/retail-proxy').ICallerContext['requestContext']
        }
    };

    const result = await pahcRequestSalesOrderCancellationAsync(retailProxyContext, request);

    if (!result) {
        throw new Error('[pahc-cancel-sales-order] Empty response from cancellation API');
    }

    return result;
}

/**
 * Data action registration so this can be referenced from module definitions.
 */
export const pahcCancelSalesOrderDataAction = createObservableDataAction({
    id: 'pahc-cancel-sales-order',
    action: <IAction<IPahcSalesOrderCancellationResponseEntity>>cancelSalesOrder,
    input: createInput
});

export default pahcCancelSalesOrderDataAction;

/**
 * Convenience helpers for imperative usage from views/components.
 */
export const requestOrderCancellation = (ctx: IActionContext, salesId: string): Promise<IPahcSalesOrderCancellationResponseEntity> =>
    cancelSalesOrder(new CancelSalesOrderInput(salesId), ctx);

export const requestSalesLineCancellation = (
    ctx: IActionContext,
    salesId: string,
    lines: IPahcSalesLine[]
): Promise<IPahcSalesOrderCancellationResponseEntity> => cancelSalesOrder(new CancelSalesOrderInput(salesId, lines), ctx);
